import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function JavaFileHandling() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const fileModules = useMemo(() => [
        {
            title: "1. Creating a File",
            code: `import java.io.File;\nimport java.io.IOException;\n\npublic class Main {\n  public static void main(String[] args) {\n    try {\n      File myFile = new File("example.txt");\n      if (myFile.createNewFile()) {\n        System.out.println("File created: " + myFile.getName());\n      } else {\n        System.out.println("File already exists.");\n      }\n    } catch (IOException e) {\n      System.out.println("An error occurred.");\n    }\n  }\n}`,
            desc: "The File class from the java.io package is used to create an object that represents a file or directory. createNewFile() returns true if the file was successfully created.",
            diagram: ""
        },
        {
            title: "2. Writing to a File",
            code: `import java.io.FileWriter;\nimport java.io.IOException;\n\npublic class Main {\n  public static void main(String[] args) {\n    try {\n      FileWriter writer = new FileWriter("example.txt");\n      writer.write("Mastering Java File I/O!");\n      writer.close(); // Crucial: Always close streams\n      System.out.println("Write successful.");\n    } catch (IOException e) {\n      System.out.println("Write failed.");\n    }\n  }\n}`,
            desc: "FileWriter is used to write characters to a file. Note: This will overwrite existing content unless you set the append flag to true in the constructor.",
            diagram: ""
        },
        {
            title: "3. Reading a File",
            code: `import java.io.File;\nimport java.util.Scanner;\n\npublic class Main {\n  public static void main(String[] args) {\n    try {\n      File file = new File("example.txt");\n      Scanner reader = new Scanner(file);\n      while (reader.hasNextLine()) {\n        System.out.println(reader.nextLine());\n      }\n      reader.close();\n    } catch (Exception e) {\n      System.out.println("Read error.");\n    }\n  }\n}`,
            desc: "The Scanner class is an easy way to read text files line by line. For larger files, BufferedReader is often preferred for better performance.",
            diagram: ""
        },
        {
            title: "4. Deleting a File",
            code: `import java.io.File;\n\npublic class Main {\n  public static void main(String[] args) {\n    File myFile = new File("example.txt");\n    if (myFile.delete()) {\n      System.out.println("Deleted: " + myFile.getName());\n    } else {\n      System.out.println("Failed to delete.");\n    }\n  }\n}`,
            desc: "The delete() method removes the file or empty directory. This action is permanent and does not move the file to a recycle bin.",
            diagram: ""
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVA FILE HANDLING (I/O)
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Introduction Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Tutorial Overview
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                            Persistence is key in software. In this tutorial, we are covering how Java interacts with your local storage. We will explore the <strong>java.io</strong> package to manage external data. By the end of this module, you will master:
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {[
                                "Creating files and directories",
                                "Writing data using FileWriters",
                                "Reading data with Scanners",
                                "Safe resource management (Closing streams)",
                                "File deletion and metadata access"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CheckIcon sx={{ color: "goldenrod", fontSize: 20 }} />
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        <Typography variant="h6" fontWeight={800} color="goldenrod" mb={1}>
                            The Concept of Streams
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9 }}>
                            Java uses the concept of <strong>Streams</strong> to handle I/O operations. Think of a stream as a sequence of data flowing from a source (reading) or to a destination (writing).
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Developer Tips</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ opacity: 0.8, pl: 1, lineHeight: 1.8 }}>
                            • <strong>Resource Leaks:</strong> Always close your writers/readers to free up system memory.<br />
                            • <strong>Error Handling:</strong> File operations almost always require <em>try-catch</em> for IOException.<br />
                            • <strong>Paths:</strong> Be mindful of absolute vs relative file paths across different OS environments.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* ✅ Learning Modules */}
                {fileModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                                {item.desc}
                            </Typography>

                            <Box sx={{ textAlign: "center", my: 2 }}>
                                {item.diagram}
                            </Box>

                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Scroll Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
